import { z } from 'zod';

/**
 * Passkey (WebAuthn/FIDO2) als zweiter Faktor — gleichrangige Alternative zu
 * TOTP (ADR 0034). Die Browser-Payloads (`options`, `attestationResponse`,
 * `assertionResponse`) werden bewusst als `z.unknown()` geführt: die
 * Struktur-/Signatur-Validierung übernimmt `@simplewebauthn/server` selbst,
 * ein eigenes Zod-Modell für dieses komplexe JSON-Format würde nur
 * Wartungsaufwand ohne Sicherheitsgewinn erzeugen.
 */

export const passkeyRegisterOptionsResponseSchema = z.object({
  challengeToken: z.string(),
  options: z.unknown(),
});
export type PasskeyRegisterOptionsResponse = z.infer<typeof passkeyRegisterOptionsResponseSchema>;

export const passkeyRegisterRequestSchema = z.object({
  challengeToken: z.string(),
  attestationResponse: z.unknown(),
  deviceLabel: z.string().trim().min(1).max(80),
});
export type PasskeyRegisterRequest = z.infer<typeof passkeyRegisterRequestSchema>;

/**
 * Passkey-Ersteinrichtung während des Logins (noch kein Access-Token — analog
 * zu `totpSetupRequestSchema`, aber für den Fall, dass der User beim allerersten
 * Login direkt Passkey statt TOTP wählt, ADR 0034 §5). Läuft über den
 * Pending-Token, nicht über eine eingeloggte Session.
 */
export const passkeySetupOptionsRequestSchema = z.object({
  pendingToken: z.string(),
});
export type PasskeySetupOptionsRequest = z.infer<typeof passkeySetupOptionsRequestSchema>;

export const passkeySetupRequestSchema = z.object({
  pendingToken: z.string(),
  challengeToken: z.string(),
  attestationResponse: z.unknown(),
  deviceLabel: z.string().trim().min(1).max(80),
});
export type PasskeySetupRequest = z.infer<typeof passkeySetupRequestSchema>;

export const passkeyLoginOptionsRequestSchema = z.object({
  pendingToken: z.string(),
});
export type PasskeyLoginOptionsRequest = z.infer<typeof passkeyLoginOptionsRequestSchema>;

export const passkeyLoginOptionsResponseSchema = z.object({
  challengeToken: z.string(),
  options: z.unknown(),
});
export type PasskeyLoginOptionsResponse = z.infer<typeof passkeyLoginOptionsResponseSchema>;

export const passkeyVerifySchema = z.object({
  pendingToken: z.string(),
  challengeToken: z.string(),
  assertionResponse: z.unknown(),
});
export type PasskeyVerifyInput = z.infer<typeof passkeyVerifySchema>;

export const passkeyListItemSchema = z.object({
  id: z.string(),
  deviceLabel: z.string(),
  transports: z.array(z.string()),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type PasskeyListItem = z.infer<typeof passkeyListItemSchema>;
